import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

const ApkUpload = ({ auth, onDone }) => {
  const [pct, setPct] = useState(null);
  const upload = async (file) => {
    if (!file) return;
    try {
      setPct(0);
      const { data } = await api.post("/admin/robox/apk/init", { filename: file.name, size: file.size }, auth);
      const n = Math.ceil(file.size / data.chunk_size);
      for (let i = 0; i < n; i++) {
        const blob = file.slice(i * data.chunk_size, (i + 1) * data.chunk_size);
        await api.put(`/admin/robox/apk/${data.upload_id}/chunk/${i}`, blob, { headers: { ...auth.headers, "Content-Type": "application/octet-stream" } });
        setPct(Math.round(((i + 1) / n) * 95));
      }
      await api.post(`/admin/robox/apk/${data.upload_id}/complete`, {}, auth);
      setPct(100); toast.success("Robo-X APK uploaded"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setTimeout(() => setPct(null), 1500); }
  };
  return (
    <div className="mt-3 flex items-center gap-3">
      <label className="inline-flex items-center gap-2 cursor-pointer rounded-md bg-[#007A8A] text-white px-4 py-2 text-sm font-semibold" data-testid="robox-apk-upload-btn">
        <Upload className="w-4 h-4" />Upload APK
        <input type="file" accept=".apk" className="hidden" disabled={pct !== null} onChange={(e) => upload(e.target.files[0])} data-testid="robox-apk-file-input" />
      </label>
      {pct !== null && <div className="flex-1 h-2 bg-slate-100 rounded" data-testid="robox-apk-progress"><div className="h-2 bg-[#007A8A] rounded transition-[width]" style={{ width: `${pct}%` }} /></div>}
    </div>
  );
};

export const RoboxAdmin = ({ auth, onErr }) => {
  const [d, setD] = useState(null);
  const [url, setUrl] = useState("");
  const load = useCallback(() => api.get("/admin/robox", auth).then((r) => { setD(r.data); setUrl(r.data.apk_url); }).catch(onErr), [auth, onErr]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try { await api.put("/admin/robox/settings", { apk_url: url }, auth); toast.success("APK link saved"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  if (!d) return <Loader2 className="w-6 h-6 animate-spin mt-8" />;
  return (
    <div className="mt-6 space-y-5" data-testid="robox-admin">
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h3 className="font-bold text-[#0A192F]">Robo-X APK file</h3>
        <p className="text-xs text-slate-500 mt-1">Upload the Robo-X .apk — it is stored securely and only buyers with a licence download link can get it. Status: <b data-testid="robox-apk-status">{d.apk_upload ? `uploaded: ${d.apk_upload.filename} (${d.apk_upload.size < 1048576 ? `${Math.ceil(d.apk_upload.size / 1024)} KB` : `${(d.apk_upload.size / 1048576).toFixed(1)} MB`}, ${new Date(d.apk_upload.uploaded_at).toLocaleString("en-IN")})` : d.apk_url ? "link set" : d.apk_file_present ? "file on server" : "NOT SET — downloads show 'being uploaded'"}</b></p>
        <ApkUpload auth={auth} onDone={load} />
        <p className="text-xs text-slate-500 mt-4">Or use an https:// link instead (used only if no file is uploaded):</p>
        <div className="flex gap-2 mt-2"><Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…/robo-x.apk" data-testid="robox-apk-url" /><Button onClick={save} className="bg-[#0A192F] text-white" data-testid="robox-apk-save">Save</Button></div>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="robox-licenses-table">
          <thead className="bg-[#F0F4F8] text-left text-xs uppercase tracking-wider text-slate-500"><tr>{["Bought", "Customer", "Licence", "Status", "Days left", "Plan", "Downloads"].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
          <tbody>{d.licenses.map((x) => (
            <tr key={x.id} className="border-t border-slate-100" data-testid={`robox-row-${x.license_key}`}>
              <td className="px-3 py-2">{new Date(x.purchased_at).toLocaleDateString("en-IN")}</td><td className="px-3 py-2">{x.name}<div className="text-xs text-slate-500">{x.email}</div></td>
              <td className="px-3 py-2 font-mono">{x.license_key}</td><td className="px-3 py-2 font-bold capitalize">{x.status}</td><td className="px-3 py-2">{x.days_left}</td>
              <td className="px-3 py-2">{x.plan || "—"}</td><td className="px-3 py-2">{x.downloads || 0}</td>
            </tr>))}
            {!d.licenses.length && <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-500">No Robo-X sales yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
